import { useEffect, useRef, useState } from "react";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  Image,
  MiBox,
  MiButton,
  MiDetailFields,
  MiDetailPageHeader,
  MiPlaceholderList,
  MiProfilePicture,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
  INPUT_TYPES,
  TIMEZONE_OPTIONS,
  phoneRegex,
  regionCodesUS,
} from "@miplatform/constants";
import { useCityAutocomplete, useEdit } from "@miplatform/hooks";
import { createUUID, getPhoneNumber } from "@miplatform/utils";
import { useCityViewModel } from "@miplatform/view-models";
import * as mdi from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import { useSession } from "@mibuildweb/hooks";
import { Box, Divider, Typography } from "@mui/material";
import { useManageMyAccountViewModel } from "./useManageMyAccountViewModel";

const MAX_LICENSES = 3;

const validationRules = [
  {
    name: "name",
    validationRule: z
      .string()
      .min(1, "Company Profile Name is required")
      .regex(/^[^:"]*$/, "Name cannot contain colons or double quotes"),
  },
  {
    name: "phoneNumber",
    validationRule: z
      .string()
      .regex(phoneRegex, "Phone number must be numeric and 10 digits")
      .optional()
      .or(z.literal("")),
  },
  {
    name: "email",
    validationRule: z
      .string()
      .email("Invalid email address.")
      .optional()
      .or(z.literal("")),
  },
  {
    name: "phoneNumber",
    validationRule: z
      .string()
      .regex(phoneRegex, "Phone number must be numeric and 10 digits")
      .optional()
      .or(z.literal("")),
  },
  {
    name: "facsimileNumber",
    validationRule: z
      .string()
      .regex(phoneRegex, "Phone number must be numeric and 10 digits")
      .optional()
      .or(z.literal("")),
  },
  {
    name: "addressLine1",
    validationRule: z
      .string()
      .regex(
        /^[^:"]*$/,
        "Address line 1 cannot contain colons or double quotes"
      )
      .optional()
      .or(z.literal("")),
  },
  {
    name: "addressLine2",
    validationRule: z
      .string()
      .regex(
        /^[^:"]*$/,
        "Address line 2 cannot contain colons or double quotes"
      )
      .optional()
      .or(z.literal("")),
  },
  {
    name: "postalCode",
    validationRule: z
      .string()
      .regex(/^\d{5}$/, "Zip Code must contain exactly 5 digits")
      .optional()
      .or(z.literal("")),
  },
  {
    name: "timeZone",
    validationRule: z.string().min(1, "Timezone is required"),
  },
];

const DEFAULT_VALUE = {
  name: "",
  email: "",
  phoneNumber: "",
  facismileNumber: "",
  addressLine1: "",
  addressLine2: "",
  regionCode: "",
  city: "",
  postalCode: "",
  invoiceTermsAndConditionsHtml: "",
  licenses: [],
  timeZone: "",
};

export const ManageMyAccount = () => {
  const {
    session: { tenantId },
  } = useSession();

  const {
    accountInfo,
    getAccountInfo,
    updateAccountInfo,
    updateWatermark,
    uploadLogo,
    deleteWatermark,
    deleteLogo,
    isLoading,
  } = useManageMyAccountViewModel({
    tenantId,
  });

  const [editMode, setEditMode] = useState(false);
  const [watermark, setWatermark] = useState(accountInfo.watermark);
  const [files, setFiles] = useState({ watermark: null, logo: null });
  const [logo, setLogo] = useState(accountInfo.logo);
  const isPictureDirty =
    typeof watermark === "string" ||
    typeof logo === "string" ||
    (accountInfo.logo && !logo) ||
    (accountInfo.watermark && !watermark);

  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const { classes } = useStyles();

  const edit = useEdit(
    { ...DEFAULT_VALUE, ...accountInfo, ...accountInfo.address },
    validationRules
  );
  const city = edit.getValue("city");
  const regionCode = edit.getValue("regionCode");

  const { cities, addCityToState, getCities } = useCityViewModel();
  const { handleCityTextChange, handleCityBlur } = useCityAutocomplete({
    update: edit.update,
    city,
    regionCode,
    addCity: addCityToState,
    cities,
  });

  const basicInfoFieldset = [
    {
      label: "Company Name",
      name: "name",
      width: "33%",
      required: true,
      readOnly: !editMode,
    },
    {
      label: "Company Legal Name",
      name: "legalName",
      width: "33%",
      readOnly: !editMode,
    },
    {
      label: "Company Email",
      name: "email",
      width: "33%",
      readOnly: !editMode,
    },
    {
      label: "Company Website",
      name: "website",
      width: "33%",
      readOnly: !editMode,
    },
    {
      label: "Company Number",
      name: "phoneNumber",
      width: "33%",
      value: getPhoneNumber(edit.getValue("phoneNumber")),
      readOnly: !editMode,
      handleChange: (e) => {
        if (
          validatePhoneNumberLength(getPhoneNumber(e.target.value), "US") ===
          "TOO_LONG"
        ) {
          return;
        }

        edit.update({
          phoneNumber: getPhoneNumber(e.target.value),
        });
      },
    },
    {
      label: "Fax Number",
      name: "facsimileNumber",
      width: "33%",
      value: getPhoneNumber(edit.getValue("facsimileNumber")),
      readOnly: !editMode,
      handleChange: (e) => {
        if (
          validatePhoneNumberLength(getPhoneNumber(e.target.value), "US") ===
          "TOO_LONG"
        ) {
          return;
        }

        edit.update({
          facsimileNumber: getPhoneNumber(e.target.value),
        });
      },
    },
    {
      label: "Address Line 1",
      name: "addressLine1",
      width: "100%",
      readOnly: !editMode,
    },
    {
      label: "Address Line 2",
      name: "addressLine2",
      width: "100%",
      readOnly: !editMode,
    },
    {
      label: "State",
      name: "regionCode",
      width: "33%",
      fieldType: INPUT_TYPES.SELECT,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      readOnly: !editMode,
      handleChange: (e) =>
        edit.update({ city: "", regionCode: e.target.value }),
    },
    {
      label: "City",
      name: "city",
      width: "33%",
      fieldType: INPUT_TYPES.AUTOCOMPLETE,
      disableClearable: true,
      options:
        cities[regionCode]?.map((c) => ({
          label: c,
          value: c,
        })) || [],
      value: city,
      onInputChange: (_, value) => handleCityTextChange(value),
      handleChange: (_, option) =>
        option && edit.update({ city: option.value }),
      onBlur: handleCityBlur,
      readOnly: !editMode,
    },
    {
      name: "postalCode",
      label: "Zip",
      width: "33%",
      readOnly: !editMode,
    },
    {
      label: "Timezone",
      name: "timeZone",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: TIMEZONE_OPTIONS,
      readOnly: !editMode,
      validationRule: z.string().min(1, "Timezone is required"),
    },
    {
      name: "invoiceTermsAndConditionsHtml",
      label: "Invoice Terms and Conditions HTML",
      width: "100%",
      readOnly: !editMode,
      multiline: true,
      rows: 4,
    },
  ];

  const licenses = edit.getValue("licenses") || [];

  const isLastOptionValid = () =>
    licenses[licenses.length - 1]?.name !== "" &&
    licenses[licenses.length - 1]?.number !== "";

  const handleAddLicense = () => {
    edit.update({
      licenses: [
        ...(licenses || []),
        {
          name: "",
          number: "",
          key: createUUID(),
          removed: false,
        },
      ],
    });
  };

  const handleDeleteLicense = (index) => {
    if (!licenses[index].key) {
      edit.update({
        licenses: licenses.filter((_, i) => i !== index),
      });
    } else {
      edit.update({
        licenses: licenses.map((option, i) =>
          i === index ? { ...option, removed: !option.removed } : option
        ),
      });
    }
  };

  const onSave = () => {
    const promises = [];
    if (edit.isDirty()) {
      promises.push(
        updateAccountInfo({
          name: edit.getValue("name"),
          legalName: edit.getValue("legalName"),
          email: edit.getValue("email"),
          phoneNumber: edit.getValue("phoneNumber"),
          facsimileNumber: edit.getValue("facsimileNumber"),
          website: edit.getValue("website"),
          invoiceTermsAndConditionsHtml: edit.getValue(
            "invoiceTermsAndConditionsHtml"
          ),
          timeZone: edit.getValue("timeZone"),
          licenses: edit
            .getValue("licenses")
            ?.filter((license) => !license.removed || license.id),
          address: {
            addressLine1: edit.getValue("addressLine1"),
            addressLine2: edit.getValue("addressLine2"),
            city: edit.getValue("city"),
            regionCode: edit.getValue("regionCode"),
            postalCode: edit.getValue("postalCode"),
          },
          modifiedOn: accountInfo.modifiedOn,
        })
      );
    }

    if (accountInfo.logo && !logo) {
      promises.push(deleteLogo());
    }
    if (accountInfo.watermark && !watermark) {
      promises.push(deleteWatermark());
    }
    if (typeof watermark === "string") {
      promises.push(
        updateWatermark({
          file: files.watermark,
        })
      );
    }
    if (typeof logo === "string") {
      promises.push(
        uploadLogo({
          file: files.logo,
          oldLogo: accountInfo.logo,
        })
      );
    }

    Promise.allSettled(promises).then(() =>
      setTimeout(() => getAccountInfo(), 200)
    );
    setEditMode(false);
    fileInputRef.current.value = "";
    logoInputRef.current.value = "";
    setFiles({ watermark: null, logo: null });
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setWatermark(accountInfo.watermark);
    setLogo(accountInfo.logo);
    fileInputRef.current.value = "";
    logoInputRef.current.value = "";
    setFiles({ watermark: null, logo: null });
    edit.reset();
  };

  const onWatermarkEdit = () => {
    if (editMode) {
      fileInputRef.current.click();
    }
  };

  const onLogoEdit = () => {
    if (editMode) {
      logoInputRef.current.click();
    }
  };

  const onWatermarkUpdate = async (event) => {
    if (event.dataTransfer || event.target) {
      const file = event.dataTransfer?.files[0] || event.target?.files[0];
      setFiles((prevFiles) => ({ ...prevFiles, watermark: file }));
      setWatermark(URL.createObjectURL(file));
    }
  };

  const onLogoUpdate = async (event) => {
    if (event.dataTransfer || event.target) {
      const file = event.dataTransfer?.files[0] || event.target?.files[0];
      setFiles((prevFiles) => ({ ...prevFiles, logo: file }));
      setLogo(URL.createObjectURL(file));
    }
  };

  const onLogoDelete = async () => setLogo(null);

  const onWatermarkDelete = async () => setWatermark(null);

  useEffect(() => {
    if (tenantId) {
      getAccountInfo();
    }
    getCities();
  }, [tenantId]);

  useEffect(() => {
    setWatermark(accountInfo.watermark);
    setLogo(accountInfo.logo);
  }, [accountInfo]);

  return (
    <>
      <MiDetailPageHeader
        title={accountInfo.name}
        titleIcon={mdi[ICONS_WEB.CHEVRON_LEFT]}
      />
      <MiBox className={classes.container}>
        <Box className={classes.userContainer}>
          <Box className={classes.userTopContainer}>
            <MiProfilePicture />
            <Box className={classes.userRoleContainer}>
              <Typography className={classes.name}>
                {accountInfo.name}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider orientation="vertical" />
        <Box className={classes.info}>
          <MiDetailFields
            headerProps={{
              icon: mdi.mdiAccount,
              title: "Basic Info",
              rightIcon: mdi[ICONS_WEB.PENCIL],
              onRightIconClick: editMode
                ? handleCancelEdit
                : () => setEditMode((prev) => !prev),
            }}
            containerStyle={classes.detailsContainer}
            detailFields={basicInfoFieldset}
            edit={edit}
          />
          <Typography className={classes.sectionText}>Licenses</Typography>
          <Divider className={classes.divider} />
          <Box paddingX={2}>
            {licenses?.map((license, index) => (
              <OptionField
                key={`option-field-${index}}`}
                option={license}
                onDelete={() => handleDeleteLicense(index)}
                isReadOnly={!editMode}
                onChange={(data) => {
                  const value = [...licenses];
                  value[index] = data;
                  edit.update({ licenses: value });
                }}
              />
            ))}
          </Box>
          <Box paddingX={4} paddingY={2}>
            {licenses.length === 0 && (
              <MiPlaceholderList
                text="No licenses"
                helperText="To add, begin editing and click + Add License button."
              />
            )}
            {editMode && (
              <MiButton
                variant={BUTTON_VARIANTS.OUTLINED}
                startIcon={{ path: mdi.mdiPlus, iconSize: 0.8 }}
                onClick={handleAddLicense}
                disabled={
                  !isLastOptionValid() ||
                  edit.getValue("licenses")?.length >= MAX_LICENSES
                }
                data-testid="add-field-btn"
                className={classes.addFieldBtn}
                title="Add License"
              />
            )}
          </Box>
          <Typography className={classes.sectionText}>
            Company Watermark
          </Typography>
          <Divider className={classes.divider} />
          <Typography className={classes.formatText}>
            Supported File Formats: PNG, JPG, JPEG
          </Typography>
          <Box className={classes.imageContainer}>
            <Image
              image={watermark}
              containerClass={classes.watermark}
              style={{
                objectFit: "cover",
                height: "200px",
                aspectRatio: "auto",
              }}
              isEditing={editMode}
              onEdit={onWatermarkEdit}
              onDelete={onWatermarkDelete}
            />
            <input
              ref={fileInputRef}
              className={classes.fileInput}
              type="file"
              accept="image/*"
              data-testid="watermark-input"
              onChange={onWatermarkUpdate}
              multiple={false}
            />
          </Box>
          <Typography className={classes.sectionText}>Company Logo</Typography>
          <Divider className={classes.divider} />
          <Typography className={classes.formatText}>
            Supported File Formats: PNG, JPG, JPEG
          </Typography>
          <Box className={classes.imageContainer}>
            <Image
              image={logo}
              containerClass={classes.watermark}
              style={{
                objectFit: "cover",
                height: "200px",
                aspectRatio: "auto",
              }}
              isEditing={editMode}
              onEdit={onLogoEdit}
              onDelete={onLogoDelete}
            />
            <input
              ref={logoInputRef}
              className={classes.fileInput}
              type="file"
              accept="image/*"
              data-testid="logo-input"
              onChange={onLogoUpdate}
              multiple={false}
            />
          </Box>
        </Box>
      </MiBox>
      <Footer
        show={edit.isDirty() || isPictureDirty}
        handleSaveClick={onSave}
        handleCancelClick={handleCancelEdit}
        disableSave={
          !edit.isValid() ||
          isLoading ||
          edit.getValue("licenses")?.some((v) => !v.name || !v.number) ||
          isLoading
        }
      />
    </>
  );
};

const OptionField = ({ option, isReadOnly, onDelete, onChange }) => {
  const { theme, classes } = useStyles();

  const optionField = [
    {
      name: `name`,
      label: "License Name",
      readOnly: isReadOnly || option?.removed,
      width: "70%",
      validationRule: z.string(),
      afterOnChange: (data) => onChange({ ...option, name: data }),
      backgroundColor: option?.removed && theme.palette.red.light,
    },
    {
      name: `number`,
      label: "License Number",
      readOnly: isReadOnly || option?.removed,
      width: "30%",
      validationRule: z.string(),
      afterOnChange: (data) => onChange({ ...option, number: data }),
      backgroundColor: option?.removed && theme.palette.red.light,
    },
  ];

  const edit = useEdit(option, optionField);

  useEffect(() => {
    edit.reset();
  }, [isReadOnly]);

  return (
    <Box className={classes.optionsContainer}>
      <Box style={{ width: "100%" }}>
        <MiDetailFields detailFields={optionField} edit={edit} />
      </Box>
      {!isReadOnly && (
        <MiButton
          data-testid="delete-field-btn"
          className={classes.addDelBtn}
          variant={BUTTON_VARIANTS.CONTAINED}
          color={option?.removed ? BUTTON_COLORS.PRIMARY : BUTTON_COLORS.RED}
          startIcon={{
            path: option?.removed ? mdi.mdiArrowULeftTop : mdi.mdiMinus,
            iconSize: 0.8,
          }}
          onClick={onDelete}
          disabled={isReadOnly}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    height: "100%",
  },
  userContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 0.4,
    padding: "1rem",
    justifyContent: "space-between",
  },
  userTopContainer: {
    marginTop: 20,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  userRoleContainer: {
    marginTop: 30,
    width: "100%",
    minWidth: "100px",
    textAlign: "center",
  },
  name: {
    fontSize: theme.fontSize.large,
    fontWeight: theme.fontWeight.bold,
  },
  detailsContainer: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
  },
  info: {
    flex: 2,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflowY: "auto",
  },
  watermark: {
    objectFit: "contain",
    height: "100%",
    maxHeight: "350px",
  },
  sectionText: {
    fontSize: theme.fontSize.large,
    fontWeight: theme.fontWeight.bold,
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  formatText: {
    color: theme.palette.grey.main,
    fontSize: theme.fontSize.small,
    marginLeft: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  imageContainer: {
    position: "relative",
    margin: theme.spacing(3),
    background:
      "linear-gradient(45deg, #ccc 26%, transparent 26%), linear-gradient(135deg, #ccc 26%, transparent 26%), linear-gradient(45deg, transparent 75%, #ccc 75%),linear-gradient(135deg, transparent 75%, #ccc 75%)",
    backgroundSize: "30px 30px",
    backgroundPosition: "0 0, 15px 0, 15px -15px, 0px 15px",
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fileInput: {
    display: "none",
  },
  optionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  addDelBtn: {
    borderRadius: "50%",
    padding: theme.spacing(0),
  },
}));
